import React, { useEffect, useRef } from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { Button, CollapseRightIcon, Panel, Section, Skeleton, Typography } from '@routific/hive/v5';
import usePermissions, { Features } from '../../../../permissions/usePermissions';
import ComponentNames from '../../../../permissions/constants/componentNames';
import { useStore } from '../../../../store/mst';
import OrderDetailsPanelHeader from '../OrderDetailsPanelHeader';
import OrderDetailsPanelFooter from '../OrderDetailsPanelFooter';
import OrderDetailsThemeBoundary from '../OrderDetailsThemeBoundary';
import { ConditionalWrapper, PanelView } from './styles';
import { OrderDetailsPanelProps } from './types';
import { getTransitionClass } from './utils';
import { useVisitDetails } from './hooks/useVisitDetails';
import { useOrderNavigation } from './hooks/useOrderNavigation';
import { DeliveryMethod } from '../../../../store/mst/models';
import OrderDetailsContent from '../Content';
import { openLinkedCustomer } from '../../../customerProfiles/customerOrderNavigation';

const OrderDetailsPanel: React.FC<OrderDetailsPanelProps> = ({
  visit: providedVisit,
  orderUuid,
  visible,
  hasTransitionedIn,
  isNextViewRoutes,
  disableActions = false,
  animate = true,
  onBack,
  onClose,
}) => {
  const { t } = useTranslation('components');
  const { ui, router, customerNotification } = useStore();
  const { previousDetailsView } = ui;
  const customerBadge = useRef<HTMLElement>();
  const restoreBadgeFocus = useRef(false);
  const selectedCustomerId = router?.queryParams?.get('customer');

  const projectId = ui.selectedWorkspace?.id as string;
  const { visit: loadedVisit, isInitialLoad, loading, retry } = useVisitDetails({ projectId, visitId: providedVisit?.id, orderUuid });
  const matchingVisit = !orderUuid || providedVisit?.uuid === orderUuid ? providedVisit : undefined;
  const visit = loadedVisit ?? matchingVisit;
  const { handleClose: closeOrder, onBackEvent } = useOrderNavigation(visit);
  const handleClose = onClose ?? closeOrder;

  const {
    canAssignStopsToRoutes,
    canEditVisits,
    canDeleteVisits,
    canCreateOptimization,
    canCreateCustomers,
    canViewCustomers,
  } = usePermissions(Features.Orders, ComponentNames.OrderDetailsPanel);

  useEffect(() => {
    if (!selectedCustomerId && restoreBadgeFocus.current) {
      customerBadge.current?.focus();
      restoreBadgeFocus.current = false;
    }
  }, [selectedCustomerId]);

  const openCustomerProfile = (source: HTMLElement) => {
    if (!visit?.customerId || !canViewCustomers) return;
    customerBadge.current = source;
    restoreBadgeFocus.current = true;
    openLinkedCustomer(router, visit.customerId);
  };

  const transitionClass = animate ? getTransitionClass({
    visible,
    hasTransitionedIn,
    isNextViewRoutes,
    previousDetailsView,
  }) : '';

  if (!visit) {
    if (!orderUuid) return null;

    const fallback = loading || !projectId ? <Section><Skeleton width="100%" /></Section> : (
      <>
        <Section><Typography role="alert">{t('customerProfilesPage.orderHistory.loadError')}</Typography></Section>
        <Section onClick={retry}><Typography tone="accent">{t('customerProfilesPage.orderHistory.retry')}</Typography></Section>
      </>
    );

    return (
      <ConditionalWrapper className={transitionClass}>
        <OrderDetailsThemeBoundary>
          <Panel
            title={t('customerProfilesPage.orderHistory.title')}
            onBack={onBack ?? onBackEvent}
            backLabel={t('visitInfoCard.back')}
            headerAction={(
              <Button
                hierarchy="tertiary"
                buttonType="icon-only"
                size="md"
                icon={<CollapseRightIcon size="md" />}
                aria-label={t('unscheduledVisits.header.close')}
                onClick={handleClose}
              />
            )}
            bordered={false}
            rounded={false}
            aria-busy={loading || !projectId}
          >
            {fallback}
          </Panel>
        </OrderDetailsThemeBoundary>
      </ConditionalWrapper>
    );
  }

  return (
    <ConditionalWrapper className={transitionClass}>
      <OrderDetailsThemeBoundary>
        <PanelView>
          <Panel
            title={visit.displayName ?? ''}
            onBack={onBack ?? (disableActions ? undefined : onBackEvent)}
            backLabel={t('visitInfoCard.back')}
            headerAction={(
              <OrderDetailsPanelHeader
                visit={visit}
                handleClose={handleClose}
                allowEdit={canEditVisits}
                allowDelete={canDeleteVisits}
                allowCreateCustomers={canCreateCustomers}
              />
            )}
            footer={!disableActions && (
              <OrderDetailsPanelFooter
                allowAssign={canAssignStopsToRoutes}
                allowOptimize={canCreateOptimization}
              />
            )}
            footerAlign="space-between"
            bordered={false}
            rounded={false}
            data-cy="order-details-panel"
            data-testid="order-details-panel"
            aria-busy={isInitialLoad}
          >
            <OrderDetailsContent
              visit={visit}
              isLoading={isInitialLoad}
              onOpenCustomerProfile={canViewCustomers ? openCustomerProfile : undefined}
              orderDetailsOptions={{
                canEditVisits: canEditVisits || false,
                deliveryMethod: (customerNotification?.cnSettings?.deliveryMethod ?? DeliveryMethod.NONE) as DeliveryMethod,
              }}
            />
          </Panel>
        </PanelView>
      </OrderDetailsThemeBoundary>
    </ConditionalWrapper>
  );
};

export default observer(OrderDetailsPanel);
